import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
export type ButtonSize = 'xs' | 'sm' | 'md'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  iconOnly?: boolean
  asChild?: boolean
}

/**
 * Exactly one variant is allowed to be a filled block of colour, and it is the
 * one that means "this is the thing to do next".
 *
 * `danger` and `success` carry their meaning in the *text*, over the same
 * neutral chrome as `secondary`, and only tint on hover. A toolbar of solid red
 * and green buttons is a permanent alarm in the periphery — after ten minutes
 * the operator stops seeing any of it, which is the opposite of what a
 * destructive action needs. Colour is never the only signal either: the label
 * says "Stop" and "Start".
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'border-line bg-s1 text-fg hover:bg-s3',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-s3 hover:text-fg',
  danger: 'border-line bg-s1 text-danger hover:border-danger/35 hover:bg-danger-soft',
  success: 'border-line bg-s1 text-ok hover:border-ok/35 hover:bg-ok-soft',
}

const SIZE: Record<ButtonSize, string> = {
  xs: 'h-7 gap-1.5 px-2 text-[12.5px] [&_svg]:size-[14px]',
  sm: 'h-8 gap-1.5 px-2.5 text-[13.5px] [&_svg]:size-[15px]',
  md: 'h-9 gap-2 px-3.5 text-[14px] [&_svg]:size-4',
}

const ICON_ONLY: Record<ButtonSize, string> = {
  xs: 'w-7 px-0',
  sm: 'w-8 px-0',
  md: 'w-9 px-0',
}

const BASE =
  'relative inline-flex shrink-0 items-center justify-center rounded-md border font-medium ' +
  'whitespace-nowrap select-none transition-colors duration-100 [&_svg]:shrink-0 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 ' +
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-45'

/**
 * A 32px control is comfortable with a mouse and a coin toss with a thumb, but
 * growing every button to 44px would wreck the density of a desktop toolbar.
 * So the *hit area* grows and the *box* does not: on coarse pointers only, a
 * centred, invisible pseudo-element stretches to at least 44×44 while the
 * button still occupies its declared size in layout.
 */
const TOUCH_TARGET =
  "[@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:top-1/2 " +
  "[@media(pointer:coarse)]:before:left-1/2 [@media(pointer:coarse)]:before:-translate-x-1/2 " +
  "[@media(pointer:coarse)]:before:-translate-y-1/2 " +
  "[@media(pointer:coarse)]:before:h-[max(100%,44px)] [@media(pointer:coarse)]:before:w-[max(100%,44px)] " +
  "[@media(pointer:coarse)]:before:content-['']"

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'sm',
    loading = false,
    iconOnly = false,
    asChild = false,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref
) {
  const classes = cn(
    BASE,
    SIZE[size],
    iconOnly && ICON_ONLY[size],
    TOUCH_TARGET,
    VARIANT[variant],
    className
  )

  // A slotted child is an arbitrary element (usually a link); `disabled` is not
  // a thing it understands, so the state is expressed with ARIA instead.
  if (asChild) {
    return (
      <Slot
        ref={ref as React.Ref<HTMLElement>}
        className={classes}
        aria-busy={loading || undefined}
        aria-disabled={disabled || loading || undefined}
        data-loading={loading ? '' : undefined}
        {...rest}
      >
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      {...rest}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  )
})
